import { useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useCityContext } from '../../hooks/useCityContext';
import { getFundingTypeBySlug } from '../../config/cities';
import { track } from '../../utils/tracking';
import { isValidCitySlug, isValidFundingTypeSlug } from '../../utils/routeGenerator';
import { generateCityFundingPageSchema } from '../../utils/schema';

// City coordinates for schema
const getCityCoordinates = (citySlug: string): { lat: string; lng: string } => {
  const coordinates: Record<string, { lat: string; lng: string }> = {
    'new-york': { lat: '40.7128', lng: '-74.0060' },
    'miami': { lat: '25.7617', lng: '-80.1918' },
    'dallas': { lat: '32.7767', lng: '-96.7970' },
    'houston': { lat: '29.7604', lng: '-95.3698' },
    'los-angeles': { lat: '34.0522', lng: '-118.2437' },
    'chicago': { lat: '41.8781', lng: '-87.6298' },
    'atlanta': { lat: '33.7490', lng: '-84.3880' },
    'phoenix': { lat: '33.4484', lng: '-112.0740' },
    'las-vegas': { lat: '36.1699', lng: '-115.1398' },
    'tampa': { lat: '27.9506', lng: '-82.4572' }
  };
  return coordinates[citySlug] || coordinates['new-york'];
};

export default function CityFunding() {
  const { citySlug, fundingSlug } = useParams<{ citySlug: string; fundingSlug: string }>();
  const { city } = useCityContext();
  const fundingType = fundingSlug ? getFundingTypeBySlug(fundingSlug) : undefined;

  // Get coordinates for schema
  const coordinates = citySlug ? getCityCoordinates(citySlug) : null;

  // FAQs specific to this funding type and city
  const faqs = city && fundingType ? getFundingFAQs(fundingType.slug, city.name) : [];

  useEffect(() => {
    if (city && fundingType) {
      track('page_view', {
        page: 'city_funding',
        city: city.name,
        state: city.state,
        funding_type: fundingType.name,
      });
    }
  }, [city, fundingType]);

  // Update document head and schema
  useEffect(() => {
    if (city && fundingType && coordinates) {
      const fundingContent = getFundingContent(fundingType.slug, city.name);
      const currentUrl = window.location.origin + window.location.pathname;
      
      document.title = `${fundingType.name} in ${city.name}, ${city.stateCode} | Business Funding Options`;
      
      // Update meta description
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute('content', `Learn about ${fundingType.name.toLowerCase()} options for ${city.name} businesses. ${fundingContent.metaDescription} Educational resources for ${city.name}, ${city.state} businesses.`);
      
      // Update meta keywords
      let metaKeywords = document.querySelector('meta[name="keywords"]');
      if (!metaKeywords) {
        metaKeywords = document.createElement('meta');
        metaKeywords.setAttribute('name', 'keywords');
        document.head.appendChild(metaKeywords);
      }
      metaKeywords.setAttribute('content', `${fundingType.name.toLowerCase()} ${city.name}, ${city.name} ${fundingType.name.toLowerCase()}, business funding ${city.name}, ${city.state} business loans, ${fundingType.slug} ${city.name}`);
      
      // Update canonical link
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
      }
      canonical.setAttribute('href', currentUrl);

      // Geo-location tags
      let geoPosition = document.querySelector('meta[name="geo.position"]');
      if (!geoPosition) {
        geoPosition = document.createElement('meta');
        geoPosition.setAttribute('name', 'geo.position');
        document.head.appendChild(geoPosition);
      }
      geoPosition.setAttribute('content', `${coordinates.lat};${coordinates.lng}`);

      let geoRegion = document.querySelector('meta[name="geo.region"]');
      if (!geoRegion) {
        geoRegion = document.createElement('meta');
        geoRegion.setAttribute('name', 'geo.region');
        document.head.appendChild(geoRegion);
      }
      geoRegion.setAttribute('content', `US-${city.stateCode}`);

      let geoPlacename = document.querySelector('meta[name="geo.placename"]');
      if (!geoPlacename) {
        geoPlacename = document.createElement('meta');
        geoPlacename.setAttribute('name', 'geo.placename');
        document.head.appendChild(geoPlacename);
      }
      geoPlacename.setAttribute('content', city.name);

      // Generate combined LocalBusiness + FAQ Schema
      const structuredData = generateCityFundingPageSchema({
        cityName: city.name,
        stateCode: city.stateCode,
        stateName: city.state,
        latitude: coordinates.lat,
        longitude: coordinates.lng,
        url: currentUrl,
        fundingType: fundingType.name,
        fundingTypeSlug: fundingType.slug,
        faqs: faqs,
        description: `Learn about ${fundingType.name.toLowerCase()} options for ${city.name} businesses. ${fundingContent.metaDescription}`
      });

      // Remove existing schema and add new one
      const existingSchema = document.querySelector('script[data-schema="city-funding-page"]');
      if (existingSchema) {
        existingSchema.remove();
      }

      const schemaScript = document.createElement('script');
      schemaScript.setAttribute('type', 'application/ld+json');
      schemaScript.setAttribute('data-schema', 'city-funding-page');
      schemaScript.textContent = JSON.stringify(structuredData);
      document.head.appendChild(schemaScript);

      // Cleanup
      return () => {
        const schemaToRemove = document.querySelector('script[data-schema="city-funding-page"]');
        if (schemaToRemove) {
          schemaToRemove.remove();
        }
        const geoTags = ['meta[name="geo.position"]', 'meta[name="geo.region"]', 'meta[name="geo.placename"]'];
        geoTags.forEach(selector => {
          const el = document.querySelector(selector);
          if (el) el.remove();
        });
      };
    }
  }, [city, fundingType, coordinates, faqs]);

  // Validate slugs
  if (!citySlug || !fundingSlug || !isValidCitySlug(citySlug) || !isValidFundingTypeSlug(fundingSlug)) {
    return <Navigate to="/business-funding" replace />;
  }

  if (!city || !fundingType) {
    return <Navigate to="/business-funding" replace />;
  }

  // Funding-specific content
  const fundingContent = getFundingContent(fundingType.slug, city.name);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Breadcrumb */}
      <div className="bg-gray-50 border-b border-gray-200 py-4 px-4">
        <div className="max-w-7xl mx-auto">
          <nav className="flex items-center gap-2 text-sm flex-wrap">
            <Link to="/" className="text-gray-600 hover:text-[#C22F3B] transition-colors">
              Home
            </Link>
            <i className="ri-arrow-right-s-line text-gray-400" />
            <Link to="/business-funding" className="text-gray-600 hover:text-[#C22F3B] transition-colors">
              Business Funding
            </Link>
            <i className="ri-arrow-right-s-line text-gray-400" />
            <Link to={`/cities/${city.slug}`} className="text-gray-600 hover:text-[#C22F3B] transition-colors">
              {city.name}
            </Link>
            <i className="ri-arrow-right-s-line text-gray-400" />
            <span className="text-gray-900 font-semibold">{fundingType.name}</span>
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-red-50 to-white py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-4xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 flex items-center justify-center bg-[#C22F3B] rounded-lg">
                <i className={`${fundingType.icon} text-white text-2xl`} />
              </div>
              <div>
                <div className="text-sm font-semibold text-[#C22F3B] uppercase tracking-wide">
                  {city.name}, {city.state}
                </div>
                <h1 className="text-4xl md:text-5xl font-bold text-gray-900">{fundingType.name}</h1>
              </div>
            </div>
            <p className="text-xl text-gray-700 mb-8 leading-relaxed">
              {fundingContent.heroDescription}
            </p>
            <Link
              to={`/cities/${city.slug}/prequalification`}
              onClick={() =>
                track('cta_clicked', {
                  cta: 'hero_prequalification',
                  city: city.name,
                  funding_type: fundingType.name,
                })
              }
              className="inline-flex items-center justify-center px-8 py-4 bg-[#C22F3B] text-white text-lg font-semibold rounded-lg hover:bg-[#99000d] transition-colors whitespace-nowrap"
            >
              Check Your Options
              <i className="ri-arrow-right-line ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Educational Notice */}
      <section className="bg-amber-50 border-y border-amber-200 py-4 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
              <i className="ri-information-line text-amber-700 text-xl" />
            </div>
            <div className="text-sm text-amber-900">
              <strong>Educational platform. Not a lender.</strong> Serving {city.name} businesses with
              information about {fundingType.name.toLowerCase()} options. No guarantee of approval. Eligibility varies by
              lender and product.
            </div>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                What is {fundingType.name}?
              </h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                {fundingContent.overview}
              </p>
              <div className="bg-red-50 border border-red-100 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Key Details</h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-money-dollar-circle-line text-[#C22F3B]" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">Funding Range</div>
                      <div className="text-gray-600">{fundingType.range}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-time-line text-[#C22F3B]" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">Typical Timeline</div>
                      <div className="text-gray-600">{fundingType.timeline}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-calendar-line text-[#C22F3B]" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">Typical Term</div>
                      <div className="text-gray-600">{fundingType.typicalTerm}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Best Used For
              </h2>
              <div className="space-y-4">
                {fundingContent.bestUsedFor.map((use, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center bg-[#C22F3B] rounded-full flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-white text-sm" />
                    </div>
                    <p className="text-gray-700 leading-relaxed">{use}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Lenders Look For */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              What Lenders Typically Review
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Understanding what lenders evaluate can help {city.name} businesses prepare for the application process.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {fundingContent.lenderCriteria.map((criterion, index) => (
              <div key={index} className="bg-white rounded-xl p-8 border border-gray-200">
                <div className="w-14 h-14 flex items-center justify-center bg-red-50 rounded-lg mb-6">
                  <i className={`${criterion.icon} text-[#C22F3B] text-2xl`} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{criterion.title}</h3>
                <p className="text-gray-600 leading-relaxed">{criterion.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Requirements Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Typical Requirements
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              While requirements vary by lender, here are common criteria for {fundingType.name.toLowerCase()} in {city.name}.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white border border-gray-200 rounded-xl p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Business Requirements</h3>
                <div className="space-y-4">
                  {fundingContent.requirements.business.map((req, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-checkbox-circle-line text-[#C22F3B]" />
                      </div>
                      <span className="text-gray-700">{req}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Documentation Needed</h3>
                <div className="space-y-4">
                  {fundingContent.requirements.documentation.map((doc, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-file-list-3-line text-[#C22F3B]" />
                      </div>
                      <span className="text-gray-700">{doc}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Timeline */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Application Process
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Understanding the typical timeline for {fundingType.name.toLowerCase()} applications.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-6">
              {fundingContent.processSteps.map((step, index) => (
                <div key={index} className="bg-white rounded-xl p-8 border border-gray-200">
                  <div className="flex items-start gap-6">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#C22F3B] text-white text-xl font-bold rounded-lg flex-shrink-0">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                        <h3 className="text-xl font-bold text-gray-900">{step.title}</h3>
                        <span className="text-sm font-semibold text-[#C22F3B] bg-red-50 px-3 py-1 rounded-full whitespace-nowrap">
                          {step.timeline}
                        </span>
                      </div>
                      <p className="text-gray-600 leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-gray-600">
              Common questions about {fundingType.name.toLowerCase()} for {city.name} businesses
            </p>
          </div>

          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <details
                key={index}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden group"
              >
                <summary className="px-8 py-6 cursor-pointer list-none flex items-center justify-between hover:bg-gray-50 transition-colors">
                  <h3 className="text-lg font-bold text-gray-900 pr-8">{faq.question}</h3>
                  <i className="ri-add-line text-2xl text-[#C22F3B] group-open:rotate-45 transition-transform flex-shrink-0" />
                </summary>
                <div className="px-8 pb-6 text-gray-600 leading-relaxed">{faq.answer}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-[#C22F3B] to-[#99000d] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Explore Your Options?</h2>
          <p className="text-xl mb-8 text-red-50">
            Complete our pre-qualification form to see what {fundingType.name.toLowerCase()} options may be available for
            your {city.name} business.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to={`/cities/${city.slug}/prequalification`}
              onClick={() =>
                track('cta_clicked', {
                  cta: 'bottom_prequalification',
                  city: city.name,
                  funding_type: fundingType.name,
                })
              }
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-[#C22F3B] text-lg font-semibold rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap"
            >
              Start Pre-Qualification
              <i className="ri-arrow-right-line ml-2" />
            </Link>
            <Link
              to={`/cities/${city.slug}`}
              className="inline-flex items-center justify-center px-8 py-4 bg-transparent border-2 border-white text-white text-lg font-semibold rounded-lg hover:bg-white/10 transition-colors whitespace-nowrap"
            >
              View All Options
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

// Helper function to get funding-specific content
function getFundingContent(fundingSlug: string, cityName: string) {
  const content: Record<string, any> = {
    'working-capital': {
      metaDescription: 'Short-term financing for day-to-day operations, inventory, and cash flow management.',
      heroDescription: `Working capital financing helps ${cityName} businesses manage day-to-day operations, purchase inventory, cover payroll, and maintain healthy cash flow during seasonal fluctuations or growth periods.`,
      overview: 'Working capital loans provide short-term financing to cover operational expenses, inventory purchases, and cash flow gaps. These loans are designed for businesses that need quick access to funds for immediate operational needs rather than long-term investments.',
      bestUsedFor: [
        'Managing seasonal cash flow fluctuations',
        'Purchasing inventory for peak seasons',
        'Covering payroll during slow periods',
        'Taking advantage of supplier discounts',
        'Bridging gaps between receivables and payables',
        'Funding short-term marketing campaigns'
      ],
      lenderCriteria: [
        {
          icon: 'ri-line-chart-line',
          title: 'Revenue Consistency',
          description: 'Lenders review monthly revenue patterns to ensure consistent cash flow. Typically require $30,000+ monthly revenue with stable or growing trends.'
        },
        {
          icon: 'ri-bank-line',
          title: 'Bank Statements',
          description: 'Recent bank statements (3-6 months) show deposit patterns, average balances, and cash flow health. Positive balances and regular deposits strengthen applications.'
        },
        {
          icon: 'ri-time-line',
          title: 'Time in Business',
          description: 'Most lenders require at least 12 months of operating history. Longer track records (24+ months) typically qualify for better terms and higher amounts.'
        },
        {
          icon: 'ri-credit-card-line',
          title: 'Credit Profile',
          description: 'Personal credit score of 600+ is typically required, with 650+ preferred for working capital.'
        },
        {
          icon: 'ri-file-list-line',
          title: 'Business Documentation',
          description: 'Valid business license, tax ID, and proof of business operations. Some lenders may request tax returns for larger amounts.'
        },
        {
          icon: 'ri-shield-check-line',
          title: 'Personal Guarantee',
          description: 'Most working capital loans require a personal guarantee from business owners with 20%+ ownership stake.'
        }
      ],
      requirements: {
        business: [
          'Minimum 12 months in business',
          'Monthly revenue of $30,000 or more',
          'Active business bank account',
          'Valid business license and tax ID',
          'No recent bankruptcies or tax liens'
        ],
        documentation: [
          '3-6 months of business bank statements',
          'Government-issued ID',
          'Business license or registration',
          'Voided business check',
          'Recent profit and loss statement (may be required)'
        ]
      },
      processSteps: [
        {
          title: 'Pre-Qualification',
          timeline: '5-10 minutes',
          description: 'Complete our online form with basic business information. Get initial feedback on potential options without impacting your credit score.'
        },
        {
          title: 'Document Submission',
          timeline: '1-2 hours',
          description: 'Upload required documents including bank statements, ID, and business license. Most documents can be submitted digitally for faster processing.'
        },
        {
          title: 'Review and Approval',
          timeline: '24-72 hours',
          description: 'Lenders review your application and documents. You may receive multiple offers to compare terms, rates, and repayment structures.'
        },
        {
          title: 'Funding',
          timeline: '1-5 business days',
          description: 'Once you accept an offer and sign agreements, funds are typically deposited directly into your business bank account within 1-5 business days.'
        }
      ]
    },
    'line-of-credit': {
      metaDescription: 'Revolving credit line for flexible access to funds as needed for ongoing business expenses.',
      heroDescription: `A business line of credit gives ${cityName} businesses flexible access to funds when needed. Draw what you need, pay interest only on what you use, and reuse the credit line as you repay—perfect for managing cash flow and unexpected expenses.`,
      overview: 'A business line of credit works like a credit card for your business. You are approved for a maximum credit limit, and you can draw funds as needed up to that limit. You only pay interest on the amount you actually use, and as you repay, the credit becomes available again.',
      bestUsedFor: [
        'Managing unpredictable cash flow needs',
        'Emergency expenses and unexpected costs',
        'Seasonal inventory purchases',
        'Bridging payment gaps with clients',
        'Taking advantage of time-sensitive opportunities',
        'Ongoing operational flexibility'
      ],
      lenderCriteria: [
        {
          icon: 'ri-credit-card-line',
          title: 'Credit Score',
          description: 'Lines of credit typically require stronger credit profiles. Personal credit score of 650+ is preferred, with 680+ qualifying for higher limits and better rates.'
        },
        {
          icon: 'ri-line-chart-line',
          title: 'Revenue History',
          description: 'Consistent monthly revenue of $30,000+ is typically required. Lenders look for stable or growing revenue patterns over 12-24 months.'
        },
        {
          icon: 'ri-bank-line',
          title: 'Banking Relationship',
          description: 'Strong banking history with positive balances and regular deposits. Some lenders prefer businesses that maintain accounts with them.'
        },
        {
          icon: 'ri-time-line',
          title: 'Business Maturity',
          description: 'Most lenders require 12-24 months in business. Longer operating history increases approval odds and credit limits.'
        },
        {
          icon: 'ri-file-chart-line',
          title: 'Financial Statements',
          description: 'Recent profit and loss statements and balance sheets may be required, especially for larger credit lines ($100K+).'
        },
        {
          icon: 'ri-shield-line',
          title: 'Collateral',
          description: 'Lines of credit may be secured or unsecured. Secured lines offer higher limits and better rates but require collateral such as equipment or receivables.'
        }
      ],
      requirements: {
        business: [
          'Minimum 12-24 months in business',
          'Monthly revenue of $30,000 or more',
          'Personal credit score of 650+',
          'Positive cash flow and profitability',
          'No recent bankruptcies or defaults'
        ],
        documentation: [
          '6-12 months of business bank statements',
          'Personal and business tax returns (1-2 years)',
          'Profit and loss statement',
          'Balance sheet',
          'Business license and tax ID',
          'Government-issued ID'
        ]
      },
      processSteps: [
        {
          title: 'Application',
          timeline: '10-15 minutes',
          description: 'Complete the application with business and financial information. Provide details about your desired credit limit and intended use.'
        },
        {
          title: 'Document Review',
          timeline: '2-3 days',
          description: 'Submit financial documents including bank statements, tax returns, and financial statements. Lenders conduct credit checks and verify information.'
        },
        {
          title: 'Underwriting',
          timeline: '3-7 days',
          description: 'Lenders evaluate your creditworthiness, cash flow, and business stability. You may be asked for additional documentation or clarification.'
        },
        {
          title: 'Approval and Access',
          timeline: '1-3 days',
          description: 'Once approved, you receive your credit limit and access to funds. Most lines of credit provide online portals or checks for easy access to your credit line.'
        }
      ]
    },
    'term-loans': {
      metaDescription: 'Fixed-amount loans with structured repayment for major investments and growth initiatives.',
      heroDescription: `Term loans provide ${cityName} businesses with fixed amounts of capital for major investments, expansion projects, or significant purchases. Structured repayment schedules and competitive rates make term loans ideal for long-term business growth.`,
      overview: 'Term loans provide a lump sum of capital that is repaid over a fixed period (typically 1-10 years) with regular monthly payments. These loans are ideal for significant investments like equipment purchases, facility expansion, or major business initiatives that generate returns over time.',
      bestUsedFor: [
        'Purchasing major equipment or machinery',
        'Expanding facilities or opening new locations',
        'Large inventory purchases',
        'Acquiring another business',
        'Refinancing existing high-interest debt',
        'Major marketing or technology investments'
      ],
      lenderCriteria: [
        {
          icon: 'ri-time-line',
          title: 'Operating History',
          description: 'Term loans typically require 24+ months in business. Longer track records (3-5+ years) qualify for larger amounts and better terms.'
        },
        {
          icon: 'ri-money-dollar-circle-line',
          title: 'Revenue Requirements',
          description: 'Minimum annual revenue requirements vary by loan size. Typically $250,000+ annually for smaller loans, $500,000+ for larger amounts.'
        },
        {
          icon: 'ri-credit-card-line',
          title: 'Credit Standards',
          description: 'Personal credit score of 650+ is typically required, with 680+ preferred for larger loans. Business credit is also evaluated if available.'
        },
        {
          icon: 'ri-file-chart-line',
          title: 'Financial Performance',
          description: 'Lenders review profitability, debt-to-income ratios, and cash flow. Strong financials with consistent profitability improve approval odds.'
        },
        {
          icon: 'ri-shield-check-line',
          title: 'Collateral',
          description: 'Larger term loans often require collateral such as equipment, real estate, or other business assets to secure the loan.'
        },
        {
          icon: 'ri-file-list-3-line',
          title: 'Business Plan',
          description: 'For larger loans or expansion projects, lenders may require a detailed business plan showing how funds will be used and expected returns.'
        }
      ],
      requirements: {
        business: [
          'Minimum 24 months in business',
          'Annual revenue of $250,000 or more',
          'Personal credit score of 650+',
          'Demonstrated profitability',
          'Strong cash flow to support debt payments',
          'Collateral for larger loan amounts'
        ],
        documentation: [
          '2-3 years of business tax returns',
          '2-3 years of personal tax returns',
          'Business financial statements (P&L, balance sheet)',
          '6-12 months of business bank statements',
          'Business plan (for larger amounts)',
          'Collateral documentation (if applicable)',
          'Business license and legal documents'
        ]
      },
      processSteps: [
        {
          title: 'Pre-Qualification',
          timeline: '15-30 minutes',
          description: 'Complete a detailed application with business information, financial details, and loan purpose. Receive initial feedback on loan amounts and terms.'
        },
        {
          title: 'Documentation',
          timeline: '3-5 days',
          description: 'Gather and submit required financial documents including tax returns, financial statements, bank statements, and business plans.'
        },
        {
          title: 'Underwriting',
          timeline: '5-14 days',
          description: 'Lenders conduct thorough review of financials, credit, collateral, and business viability. May request additional documentation or clarification.'
        },
        {
          title: 'Approval and Funding',
          timeline: '3-7 days',
          description: 'Once approved, review and sign loan agreements. Funds are typically disbursed within 3-7 days after final approval and document signing.'
        }
      ]
    },
    'equipment-financing': {
      metaDescription: 'Asset-backed financing for purchasing or leasing business equipment and machinery.',
      heroDescription: `Equipment financing helps ${cityName} businesses acquire the machinery, vehicles, and technology needed to operate and grow. The equipment itself serves as collateral, making this an accessible option for businesses investing in productive assets.`,
      overview: 'Equipment financing is specifically designed for purchasing business equipment, machinery, vehicles, or technology. The equipment being purchased serves as collateral for the loan, which often results in more favorable terms and higher approval rates compared to unsecured financing.',
      bestUsedFor: [
        'Purchasing manufacturing equipment',
        'Acquiring commercial vehicles',
        'Buying restaurant or retail equipment',
        'Investing in technology and computers',
        'Purchasing medical or dental equipment',
        'Acquiring construction machinery'
      ],
      lenderCriteria: [
        {
          icon: 'ri-tools-line',
          title: 'Equipment Value',
          description: 'Lenders finance 80-100% of equipment value depending on type and condition. New equipment typically qualifies for higher financing percentages than used equipment.'
        },
        {
          icon: 'ri-time-line',
          title: 'Business History',
          description: 'Minimum 12-24 months in business is typically required. Startups may qualify if purchasing essential equipment with strong business plans.'
        },
        {
          icon: 'ri-credit-card-line',
          title: 'Credit Requirements',
          description: 'Personal credit score of 600+ is typically acceptable for equipment financing. The equipment collateral reduces credit requirements compared to unsecured loans.'
        },
        {
          icon: 'ri-money-dollar-circle-line',
          title: 'Down Payment',
          description: 'Down payments range from 0-20% depending on creditworthiness, equipment type, and lender. Stronger credit profiles may qualify for 100% financing.'
        },
        {
          icon: 'ri-file-list-line',
          title: 'Equipment Documentation',
          description: 'Detailed quotes, invoices, or purchase agreements for the equipment. Lenders verify equipment specifications, condition, and fair market value.'
        },
        {
          icon: 'ri-line-chart-line',
          title: 'Cash Flow',
          description: 'Lenders verify that business cash flow can support equipment payments. The equipment should generate revenue or savings to justify the investment.'
        }
      ],
      requirements: {
        business: [
          'Minimum 12 months in business (varies by lender)',
          'Personal credit score of 600+',
          'Sufficient cash flow to support payments',
          'Valid business license and tax ID',
          'Equipment quote or purchase agreement'
        ],
        documentation: [
          'Equipment quote, invoice, or purchase agreement',
          '3-6 months of business bank statements',
          'Business tax returns (1-2 years)',
          'Personal tax returns (may be required)',
          'Business license and registration',
          'Government-issued ID',
          'Proof of insurance (may be required)'
        ]
      },
      processSteps: [
        {
          title: 'Equipment Selection',
          timeline: '1-3 days',
          description: 'Identify the equipment you need and obtain detailed quotes from vendors. Ensure quotes include specifications, pricing, and delivery timelines.'
        },
        {
          title: 'Application',
          timeline: '15-30 minutes',
          description: 'Complete the financing application with business information and equipment details. Submit equipment quotes and required business documents.'
        },
        {
          title: 'Approval',
          timeline: '1-5 days',
          description: 'Lenders review your application, credit, and equipment value. Equipment financing typically has faster approval than other loan types due to collateral.'
        },
        {
          title: 'Funding and Delivery',
          timeline: '3-7 days',
          description: 'Once approved, lender pays vendor directly or deposits funds. You receive equipment and begin making scheduled payments according to your agreement.'
        }
      ]
    },
    'commercial-real-estate': {
      metaDescription: 'Long-term financing for purchasing or refinancing commercial property and buildings.',
      heroDescription: `Commercial real estate loans help ${cityName} businesses purchase, refinance, or develop commercial properties. Whether buying your business location, investment properties, or expanding facilities, CRE loans provide long-term financing solutions.`,
      overview: 'Commercial real estate (CRE) loans are used to purchase, refinance, or develop commercial properties including office buildings, retail spaces, warehouses, industrial facilities, and multi-family properties. These loans typically have longer terms (5-25 years) and are secured by the property itself.',
      bestUsedFor: [
        'Purchasing owner-occupied business property',
        'Buying investment commercial properties',
        'Refinancing existing commercial mortgages',
        'Developing or renovating commercial spaces',
        'Acquiring multi-family properties (5+ units)',
        'Building or expanding business facilities'
      ],
      lenderCriteria: [
        {
          icon: 'ri-building-line',
          title: 'Property Value',
          description: 'Lenders finance 65-80% of property value (loan-to-value ratio). Down payments of 20-35% are typically required. Property appraisals determine fair market value.'
        },
        {
          icon: 'ri-money-dollar-circle-line',
          title: 'Debt Service Coverage',
          description: 'Property income must exceed debt payments by 1.25-1.5x (DSCR). Lenders analyze rental income, lease agreements, and property cash flow.'
        },
        {
          icon: 'ri-credit-card-line',
          title: 'Credit Requirements',
          description: 'Personal credit score of 680+ is typically required for commercial real estate loans. Business credit and financial strength are also heavily weighted.'
        },
        {
          icon: 'ri-time-line',
          title: 'Business Experience',
          description: 'Lenders prefer 2-5+ years in business with strong financial performance. Real estate investment experience is valued for investment properties.'
        },
        {
          icon: 'ri-file-chart-line',
          title: 'Financial Strength',
          description: 'Strong business and personal financials are required. Lenders review tax returns, financial statements, liquidity, and net worth.'
        },
        {
          icon: 'ri-file-list-3-line',
          title: 'Property Documentation',
          description: 'Extensive property documentation including appraisals, environmental reports, title work, surveys, and lease agreements (for investment properties).'
        }
      ],
      requirements: {
        business: [
          'Minimum 2-3 years in business',
          'Personal credit score of 680+',
          'Down payment of 20-35%',
          'Strong business and personal financials',
          'Debt service coverage ratio of 1.25x+',
          'Sufficient liquidity and reserves'
        ],
        documentation: [
          '2-3 years of business tax returns',
          '2-3 years of personal tax returns',
          'Business financial statements',
          'Personal financial statement',
          'Property appraisal',
          'Environmental report (Phase I)',
          'Lease agreements (for investment properties)',
          'Purchase agreement or letter of intent',
          'Business plan (for development projects)'
        ]
      },
      processSteps: [
        {
          title: 'Pre-Qualification',
          timeline: '1-2 weeks',
          description: 'Submit initial application with business and property information. Lender reviews financials and provides preliminary loan terms and requirements.'
        },
        {
          title: 'Property Due Diligence',
          timeline: '3-6 weeks',
          description: 'Order property appraisal, environmental report, title search, and survey. Lender reviews property condition, value, and any potential issues.'
        },
        {
          title: 'Underwriting',
          timeline: '4-8 weeks',
          description: 'Comprehensive review of all financial documents, property reports, and loan structure. Lender may request additional documentation or clarification.'
        },
        {
          title: 'Closing and Funding',
          timeline: '1-2 weeks',
          description: 'Final loan approval, document signing, and closing. Funds are disbursed at closing, and property ownership transfers to buyer.'
        }
      ]
    }
  };

  return content[fundingSlug] || content['working-capital'];
}

// Helper function to get funding-specific FAQs
function getFundingFAQs(fundingSlug: string, cityName: string) {
  const faqs: Record<string, Array<{ question: string; answer: string }>> = {
    'working-capital': [
      {
        question: `How quickly can ${cityName} businesses get working capital?`,
        answer: 'Working capital loans are among the fastest funding options. Pre-qualification takes 5-10 minutes, and once documents are submitted, approval typically occurs within 24-72 hours. Funding can be deposited in your business bank account within 1-5 business days after approval.'
      },
      {
        question: `What credit score is needed for working capital in ${cityName}?`,
        answer: 'Most lenders require a personal credit score of 600 or higher, with 650+ preferred for better terms and rates. Some alternative lenders may work with lower scores but typically offer higher rates and shorter terms.'
      },
      {
        question: `How much working capital can ${cityName} businesses qualify for?`,
        answer: 'Working capital amounts typically range from $10,000 to $500,000. The amount you qualify for depends on monthly revenue, time in business, credit profile, and cash flow. Many lenders offer 10-20% of annual revenue as a general guideline.'
      },
      {
        question: 'What documents are needed for working capital?',
        answer: 'Basic requirements include 3-6 months of business bank statements, government-issued ID, business license or registration, and a voided business check. Some lenders may request tax returns or profit and loss statements for larger amounts.'
      },
      {
        question: 'How is working capital repaid?',
        answer: 'Repayment structures vary by lender. Common options include daily or weekly ACH debits from your business bank account, monthly payments, or percentage-of-revenue payments. Terms typically range from 3-18 months.'
      }
    ],
    'line-of-credit': [
      {
        question: `How does a business line of credit work for ${cityName} businesses?`,
        answer: 'A business line of credit provides access to a maximum credit limit that you can draw from as needed. You only pay interest on the amount you actually use, and as you repay, the credit becomes available again—similar to a credit card but with business-focused terms and typically lower rates.'
      },
      {
        question: `What are the credit requirements for a line of credit in ${cityName}?`,
        answer: 'Lines of credit typically require stronger credit profiles than working capital loans. Most lenders prefer personal credit scores of 650+, with 680+ qualifying for higher limits and better rates. Business credit is also reviewed if available.'
      },
      {
        question: `How much can ${cityName} businesses access with a line of credit?`,
        answer: 'Business lines of credit typically range from $10,000 to $250,000, though some lenders offer higher limits for well-qualified businesses. The amount depends on revenue, credit profile, time in business, and financial strength.'
      },
      {
        question: 'What is the difference between secured and unsecured lines of credit?',
        answer: 'Unsecured lines of credit do not require collateral but typically have lower limits and higher rates. Secured lines are backed by collateral (equipment, receivables, or other assets) and offer higher limits and better rates, but put your assets at risk if you default.'
      },
      {
        question: 'How long does it take to get approved for a line of credit?',
        answer: 'The application process typically takes 10-15 minutes. Document review and underwriting take 3-10 days depending on the lender and complexity. Once approved, you typically receive access to your credit line within 1-3 days.'
      }
    ],
    'term-loans': [
      {
        question: `What are term loans best used for in ${cityName}?`,
        answer: 'Term loans are ideal for significant investments that generate returns over time, such as purchasing equipment, expanding facilities, acquiring inventory, refinancing debt, or funding major business initiatives. The fixed repayment schedule makes budgeting predictable.'
      },
      {
        question: `How much can ${cityName} businesses borrow with term loans?`,
        answer: 'Term loans typically range from $50,000 to $5,000,000 depending on business size, revenue, creditworthiness, and loan purpose. Larger loans require stronger financials, longer operating history, and often collateral.'
      },
      {
        question: `What credit score is needed for a term loan in ${cityName}?`,
        answer: 'Most lenders require a personal credit score of 650 or higher for term loans, with 680+ preferred for larger amounts and better rates. Business credit, financial performance, and collateral also significantly impact approval and terms.'
      },
      {
        question: 'How long are term loan repayment periods?',
        answer: 'Term loan repayment periods typically range from 1-10 years depending on loan amount and purpose. Shorter terms (1-3 years) are common for smaller loans or working capital needs. Longer terms (5-10 years) are typical for equipment purchases or major investments.'
      },
      {
        question: 'What documents are required for term loans?',
        answer: 'Term loans require more extensive documentation than short-term financing. Typical requirements include 2-3 years of business and personal tax returns, financial statements (P&L and balance sheet), 6-12 months of bank statements, business plan (for larger amounts), and collateral documentation if applicable.'
      }
    ],
    'equipment-financing': [
      {
        question: `What types of equipment can ${cityName} businesses finance?`,
        answer: 'Equipment financing covers a wide range of business equipment including manufacturing machinery, commercial vehicles, restaurant equipment, medical devices, technology and computers, construction equipment, office furniture, and more. Both new and used equipment can typically be financed.'
      },
      {
        question: `How much down payment is required for equipment financing in ${cityName}?`,
        answer: 'Down payment requirements vary from 0-20% depending on creditworthiness, equipment type, and lender. Well-qualified businesses may secure 100% financing for new equipment. Used equipment or lower credit scores typically require 10-20% down.'
      },
      {
        question: `Can ${cityName} startups get equipment financing?`,
        answer: 'Yes, startups can qualify for equipment financing more easily than other loan types because the equipment serves as collateral. However, lenders typically require strong personal credit (650+), a solid business plan, and may require larger down payments for newer businesses.'
      },
      {
        question: 'What is the difference between equipment financing and equipment leasing?',
        answer: 'Equipment financing is a loan where you own the equipment and make payments until paid off. Equipment leasing is a rental agreement where you make payments to use the equipment but do not own it. Financing builds equity and allows depreciation deductions, while leasing offers lower payments and easier upgrades.'
      },
      {
        question: 'How quickly can equipment financing be approved?',
        answer: 'Equipment financing typically has faster approval than other loan types due to the collateral. Applications take 15-30 minutes, approval decisions are often made within 1-5 days, and funding occurs within 3-7 days after approval. The equipment vendor is usually paid directly by the lender.'
      }
    ],
    'commercial-real-estate': [
      {
        question: `What types of properties qualify for CRE loans in ${cityName}?`,
        answer: 'Commercial real estate loans cover office buildings, retail spaces, warehouses, industrial facilities, multi-family properties (5+ units), mixed-use buildings, hotels, and special-purpose properties. Both owner-occupied and investment properties can be financed.'
      },
      {
        question: `How much down payment is required for commercial real estate in ${cityName}?`,
        answer: 'Commercial real estate loans typically require 20-35% down payment depending on property type, borrower strength, and loan program. Owner-occupied properties may qualify for lower down payments (as low as 10% with SBA loans) compared to investment properties.'
      },
      {
        question: `What is debt service coverage ratio (DSCR) and why does it matter?`,
        answer: 'DSCR measures whether property income covers debt payments. Lenders typically require DSCR of 1.25-1.5x, meaning property income must exceed annual debt payments by 25-50%. For example, if annual debt payments are $100,000, property income should be $125,000-$150,000.'
      },
      {
        question: `How long does the commercial real estate loan process take in ${cityName}?`,
        answer: 'Commercial real estate loans typically take 60-90 days from application to closing. The timeline includes pre-qualification (1-2 weeks), property due diligence and appraisal (3-6 weeks), underwriting (4-8 weeks), and closing (1-2 weeks). Complex properties or transactions may take longer.'
      },
      {
        question: 'What are typical commercial real estate loan terms?',
        answer: 'Commercial real estate loans typically have terms of 5-25 years. Many loans have amortization periods of 20-25 years with balloon payments due after 5-10 years, requiring refinancing. Interest rates may be fixed for the entire term or adjust after an initial fixed period.'
      }
    ]
  };

  return faqs[fundingSlug] || faqs['working-capital'];
}
